import { EVENTS } from '../../utils/constants';

/**
 * Load-more pagination for the collection grid, based on the blog's
 * initLoadMore but delegation-based: the filter JS replaces the
 * [data-pagination] contents wholesale on every facet change, so listeners
 * must live on the wrapper, not the button.
 */

const fetchPageDoc = async (nextUrl, sectionId) => {
  const url = new URL(nextUrl, window.location.origin);
  if (sectionId) {
    url.searchParams.set('section_id', sectionId);
  }
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Loading more products failed with status ${response.status}`);
  }
  const html = await response.text();
  return new DOMParser().parseFromString(html, 'text/html');
};

const appendProducts = (gridItemsEl, resultsDoc) => {
  const nextItemsEl = resultsDoc.querySelector('[data-product-grid-items]');
  if (!nextItemsEl) {
    throw new Error('Loading more products returned an unexpected response');
  }
  const cardEls = nextItemsEl.querySelectorAll(':scope > *');
  cardEls.forEach(cardEl => gridItemsEl.appendChild(cardEl));
};

const renderNextPagination = (paginationEl, resultsDoc) => {
  const nextPaginationEl = resultsDoc.querySelector('[data-pagination]');
  if (nextPaginationEl) {
    paginationEl.innerHTML = nextPaginationEl.innerHTML;
  }
  // Keep keyboard focus in the pagination flow: the activated button was
  // just replaced, so focus its successor, or the count on the last page.
  // preventScroll keeps the viewport where the user was — the appended cards
  // push the pagination far down, and focus() would otherwise leap past them.
  const newLoadMoreEl = paginationEl.querySelector('[data-load-more]');
  if (newLoadMoreEl) {
    newLoadMoreEl.focus({ preventScroll: true });
    return;
  }
  const countEl = paginationEl.querySelector('[data-product-count]');
  if (countEl) {
    countEl.setAttribute('tabindex', '-1');
    countEl.addEventListener('blur', () => countEl.removeAttribute('tabindex'), { once: true });
    countEl.focus({ preventScroll: true });
  }
};

const initLoadMore = () => {
  const sectionEl = document.querySelector('[data-collection-section]');
  if (!sectionEl || sectionEl.dataset.loadMoreInit) return;
  sectionEl.dataset.loadMoreInit = 'true';

  const paginationEl = sectionEl.querySelector('[data-pagination]');
  if (!paginationEl) return;

  let isLoading = false;
  // Bumped on every grid re-render (ours included) so a load-more response
  // resolving after a filter change is discarded instead of appending stale
  // unfiltered products into the fresh grid.
  let renderGeneration = 0;
  document.addEventListener(EVENTS.COLLECTION_RERENDER, () => {
    renderGeneration += 1;
  });

  const loadNextPage = async (loadMoreEl) => {
    const gridItemsEl = sectionEl.querySelector('[data-product-grid-items]');
    if (isLoading || !loadMoreEl.dataset.nextUrl || !gridItemsEl) return;

    isLoading = true;
    const startGeneration = renderGeneration;
    loadMoreEl.setAttribute('aria-disabled', 'true');
    try {
      const resultsDoc = await fetchPageDoc(loadMoreEl.dataset.nextUrl, sectionEl.dataset.sectionId);
      if (renderGeneration !== startGeneration) return;

      appendProducts(gridItemsEl, resultsDoc);
      renderNextPagination(paginationEl, resultsDoc);
      if (typeof window.ReloadSmartWishlist === 'function') {
        ReloadSmartWishlist();
      }
      document.dispatchEvent(new CustomEvent(EVENTS.COLLECTION_RERENDER));
    } catch (error) {
      console.error(error);
      loadMoreEl.removeAttribute('aria-disabled');
    } finally {
      isLoading = false;
    }
  };

  paginationEl.addEventListener('click', (event) => {
    const loadMoreEl = event.target.closest('[data-load-more]');
    if (!loadMoreEl) return;
    event.preventDefault();
    loadNextPage(loadMoreEl);
  });
};

export default initLoadMore;
